import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, HandCoins, CreditCard, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "./shared";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const SOURCE_ACCOUNTS = ["Salon Business Bank Account", "Salon Cash Drawer (Counter)", "Papa Ji's Account / UPI", "Mummy Ji's Account / UPI", "Personal Self Account / UPI"];
const CHEQUE_STYLES = {
  issued: "bg-[#C5A059]/20 text-[#8a6d33]",
  cleared: "bg-emerald-600/10 text-emerald-700",
  bounced: "bg-red-100 text-red-600",
};
const emptyCard = { name: "", statement_day: "1", due_day: "15", notes: "" };

export const FinanceCards = ({ cheques, onChequeStatus, isCeo }) => {
  const [cards, setCards] = useState(null);
  const [family, setFamily] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCard);
  const [paying, setPaying] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", source_account: SOURCE_ACCOUNTS[0], note: "" });
  const [settling, setSettling] = useState(null);
  const [settleForm, setSettleForm] = useState({ amount: "", note: "" });

  const load = useCallback(() => {
    api.get("/credit-cards").then(({ data }) => setCards(data)).catch(() => {});
    if (isCeo) api.get("/family/ledger").then(({ data }) => setFamily(data)).catch(() => {});
  }, [isCeo]);
  useEffect(() => { load(); }, [load]);

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const saveCard = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Card name required"); return; }
    const payload = { ...form, statement_day: Number(form.statement_day) || 1, due_day: Number(form.due_day) || 15 };
    try {
      if (editing) await api.put(`/credit-cards/${editing.id}`, payload);
      else await api.post("/credit-cards", payload);
      toast.success(editing ? "Card updated" : "Card added");
      setOpen(false); setEditing(null); load();
    } catch (e2) { err(e2); }
  };

  const removeCard = async (id) => {
    try { await api.delete(`/credit-cards/${id}`); toast.success("Card deleted"); load(); } catch (e2) { err(e2); }
  };

  const payCard = async (e) => {
    e.preventDefault();
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/credit-cards/${paying.id}/payment`, { amount: amt, source_account: payForm.source_account, note: payForm.note });
      toast.success("Card bill payment recorded");
      setPaying(null); setPayForm({ amount: "", source_account: SOURCE_ACCOUNTS[0], note: "" }); load();
    } catch (e2) { err(e2); }
  };

  const settle = async (e) => {
    e.preventDefault();
    const amt = Number(settleForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post("/family/settle", { account: settling.account, amount: amt, note: settleForm.note });
      toast.success(`Settled ${fmt(amt)} with ${settling.account}`);
      setSettling(null); setSettleForm({ amount: "", note: "" }); load();
    } catch (e2) { err(e2); }
  };

  const dueBanners = (cards?.rows || []).filter((c) => c.balance > 0 && c.days_to_due <= 3);

  return (
    <div className="space-y-6" data-testid="cards-cheques-tab">
      {dueBanners.map((c) => (
        <div key={c.id} className="bg-purple-50 border border-purple-200 rounded-md px-4 py-3 text-sm text-purple-800 font-semibold" data-testid={`card-due-banner-${c.id}`}>
          💳 {c.name} — {fmt(c.balance)} payable by {c.next_due} ({c.days_to_due <= 0 ? "today / overdue" : `${c.days_to_due} day${c.days_to_due > 1 ? "s" : ""} left`}). Keep the bank balance ready.
        </div>
      ))}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-bold text-[#1C1C1C] flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#C5A059]" /> Credit Cards Manager</h2>
          <button onClick={() => { setEditing(null); setForm(emptyCard); setOpen(true); }} data-testid="add-card-btn"
            className="bg-[#0A3F2F] text-white text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-1.5 hover:bg-[#0d5540]">
            <Plus className="w-3.5 h-3.5" /> Add Card
          </button>
        </div>
        {cards && cards.unassigned_charges > 0 && (
          <p className="text-xs text-[#8a6d33] mb-2" data-testid="unassigned-charges">₹{cards.unassigned_charges.toLocaleString("en-IN")} of credit-card expenses are not linked to a saved card — edit those expenses to pick a card.</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cards-grid">
          {(!cards || cards.rows.length === 0) && <p className="text-sm text-[#5C6A63]" data-testid="no-cards">No cards saved yet. Add your credit cards to track dues and due dates.</p>}
          {cards?.rows.map((c) => (
            <div key={c.id} className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid={`card-row-${c.id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-bold text-[#1C1C1C]">{c.name}</h3>
                <div className="flex gap-2">
                  <button onClick={() => { setEditing(c); setForm({ name: c.name, statement_day: String(c.statement_day), due_day: String(c.due_day), notes: c.notes || "" }); setOpen(true); }}
                    data-testid={`edit-card-${c.id}`} className="text-[#3D4A44] hover:text-[#0A3F2F]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => removeCard(c.id)} data-testid={`delete-card-${c.id}`} className="text-[#3D4A44] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className={`text-2xl font-bold mt-2 ${c.balance > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid={`card-balance-${c.id}`}>{fmt(c.balance)}</p>
              <p className="text-[11px] text-[#5C6A63]">charged {fmt(c.charges)} · paid {fmt(c.paid)}</p>
              <p className="text-xs text-[#5C6A63] mt-2">Statement day {c.statement_day} · Due day {c.due_day} · next due <span className="font-semibold text-[#1C1C1C]">{c.next_due}</span></p>
              {c.balance > 0 && (
                <button onClick={() => { setPaying(c); setPayForm({ amount: String(c.balance), source_account: SOURCE_ACCOUNTS[0], note: "" }); }}
                  data-testid={`pay-card-${c.id}`}
                  className="mt-3 w-full bg-[#C5A059] text-[#1C1C1C] text-xs font-bold py-2 rounded-full hover:bg-[#d4b06c] flex items-center justify-center gap-1.5">
                  <HandCoins className="w-3.5 h-3.5" /> Pay Card Bill
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-bold text-[#1C1C1C] mb-3">🏦 Cheques Issued (PDC Tracker)</h2>
        <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                <th className="px-4 py-3 font-semibold">Cheque No</th>
                <th className="px-4 py-3 font-semibold">Cheque Date</th>
                <th className="px-4 py-3 font-semibold">For</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="pdc-table-body">
              {cheques.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C6A63]" data-testid="no-pdc">No cheque payments in this range.</td></tr>}
              {cheques.map((e) => (
                <tr key={e.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`pdc-row-${e.id}`}>
                  <td className="px-4 py-3 font-semibold">{e.cheque_no ? `#${e.cheque_no}` : "—"}</td>
                  <td className="px-4 py-3">{e.cheque_date || "—"}</td>
                  <td className="px-4 py-3 text-[#5C6A63]">{e.category}{e.description ? ` — ${e.description}` : ""}</td>
                  <td className="px-4 py-3 font-bold text-red-600">{fmt(e.amount)}</td>
                  <td className="px-4 py-3">
                    <span data-testid={`pdc-status-${e.id}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${CHEQUE_STYLES[e.cheque_status] || CHEQUE_STYLES.issued}`}>
                      {e.cheque_status === "issued" ? "Pending Clearance" : e.cheque_status || "Pending Clearance"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      {e.cheque_status === "issued" && (
                        <>
                          <button onClick={() => onChequeStatus(e, "cleared")} data-testid={`pdc-clear-${e.id}`} className="text-[10px] font-bold text-emerald-700 underline underline-offset-2">Mark Cleared</button>
                          <button onClick={() => onChequeStatus(e, "bounced")} data-testid={`pdc-bounce-${e.id}`} className="text-[10px] font-bold text-red-600 underline underline-offset-2">Bounced</button>
                        </>
                      )}
                      {e.cheque_status === "bounced" && (
                        <button onClick={() => onChequeStatus(e, "issued")} data-testid={`pdc-reissue-${e.id}`} className="text-[10px] font-bold text-[#8a6d33] underline underline-offset-2">Re-issue</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#5C6A63] mt-2">Amounts are deducted from your figures only when a cheque is marked Cleared — bounced cheques are excluded automatically.</p>
      </div>

      {isCeo && family && (
        <div>
          <h2 className="text-sm font-bold text-[#1C1C1C] mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#C5A059]" /> Joint Family Settlement</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="family-cards">
            {family.accounts.map((a) => (
              <div key={a.account} className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid={`family-card-${a.account.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-[#1C1C1C]">{a.account}</h3>
                    <p className="text-[11px] text-[#5C6A63]">salon expenses paid {fmt(a.owed)} · returned {fmt(a.settled)}</p>
                  </div>
                  <p className={`text-xl font-bold ${a.balance > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid={`family-balance-${a.account.replace(/\s+/g, "-").toLowerCase()}`}>{fmt(a.balance)}</p>
                </div>
                {a.entries.length > 0 && (
                  <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                    {a.entries.slice(0, 6).map((en) => (
                      <div key={en.id} className="flex justify-between text-xs text-[#5C6A63]">
                        <span className="truncate">{en.date} · {en.description}</span>
                        <span className={`font-semibold ${en.direction === "settled" ? "text-emerald-700" : "text-red-600"}`}>{en.direction === "settled" ? "-" : "+"}{fmt(en.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {a.balance > 0 && (
                  <button onClick={() => { setSettling(a); setSettleForm({ amount: String(a.balance), note: "" }); }} data-testid={`settle-family-${a.account.replace(/\s+/g, "-").toLowerCase()}`}
                    className="mt-3 w-full bg-[#0A3F2F] text-white text-xs font-bold py-2 rounded-full hover:bg-[#0d5540]">
                    Settle / Return Cash
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-[#5C6A63] mt-2">When a salon expense is paid from Papa Ji or Mummy Ji's account, it counts in salon profit but the amount owed to them collects here.</p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editing ? "Edit Card" : "Add Credit Card"}</DialogTitle></DialogHeader>
          <form onSubmit={saveCard} className="space-y-3" data-testid="card-form">
            <input data-testid="card-name-input" className={inputCls} placeholder="Card name (e.g. HDFC Regalia) *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Statement date (day)</label>
                <input data-testid="card-statement-input" type="number" min="1" max="31" className={inputCls} value={form.statement_day} onChange={(e) => setForm({ ...form, statement_day: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Payment due (day)</label>
                <input data-testid="card-due-input" type="number" min="1" max="31" className={inputCls} value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} />
              </div>
            </div>
            <input data-testid="card-notes-input" className={inputCls} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <button type="submit" data-testid="card-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {editing ? "Save Changes" : "Add Card"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paying} onOpenChange={(v) => !v && setPaying(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pay Card Bill — {paying?.name}</DialogTitle></DialogHeader>
          <form onSubmit={payCard} className="space-y-3" data-testid="card-pay-form">
            <div className="bg-[#FAFAF8] rounded-md p-3 text-sm flex justify-between">
              <span className="text-[#5C6A63]">Current balance</span><span className="font-bold text-red-600">{fmt(paying?.balance)}</span>
            </div>
            <input data-testid="card-pay-amount-input" type="number" min="1" step="any" className={inputCls} placeholder="Amount ₹" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
            <select data-testid="card-pay-source-select" className={inputCls} value={payForm.source_account} onChange={(e) => setPayForm({ ...payForm, source_account: e.target.value })}>
              {SOURCE_ACCOUNTS.map((a) => <option key={a}>{a}</option>)}
            </select>
            <input data-testid="card-pay-note-input" className={inputCls} placeholder="Note (optional)" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
            <button type="submit" data-testid="card-pay-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Payment
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!settling} onOpenChange={(v) => !v && setSettling(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Settle / Return Cash — {settling?.account}</DialogTitle></DialogHeader>
          <form onSubmit={settle} className="space-y-3" data-testid="family-settle-form">
            <div className="bg-[#FAFAF8] rounded-md p-3 text-sm flex justify-between">
              <span className="text-[#5C6A63]">Balance owed</span><span className="font-bold text-red-600">{fmt(settling?.balance)}</span>
            </div>
            <input data-testid="settle-amount-input" type="number" min="1" step="any" className={inputCls} placeholder="Amount returning ₹" value={settleForm.amount} onChange={(e) => setSettleForm({ ...settleForm, amount: e.target.value })} />
            <input data-testid="settle-note-input" className={inputCls} placeholder="Note (optional)" value={settleForm.note} onChange={(e) => setSettleForm({ ...settleForm, note: e.target.value })} />
            <button type="submit" data-testid="settle-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Settlement
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
